import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';
import clsx from 'clsx';

import { makeStyles } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Accordian from '@mui/material/Accordion';
import AccordianSummary from '@mui/material/AccordionSummary';
import AccordianDetails from '@mui/material/AccordionDetails';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';

import Push from '../../../components/push/';
import Syslog from '../../../components/system/syslog';
import ESRest from '../../../components/system/es';
import Raw from '../../../components/system/raw';
import Zeek from '../../../components/system/zeek';
import Netflow from '../../../components/system/netflow';
import IPFix from '../../../components/system/ipfix';
import Kafka from '../../../components/system/kafka';

import PolicyService from '../../../data/policy-service';
import defaultConfigs from '../../../data/eexportd';
import Auth from '../../../data/auth-service';

import Animations from '../../../libraries/animations';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    appbarItem: {
        lineHeight: '48px',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        '& > .MuiBox-root': {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
        }
    },
    tabBar: {
        background: theme.styled.paper.background,
        color: theme.palette.primary.main,
    },
    tabBG: {
        background: theme.palette.background.default,
    },
    tab: {
        paddingLeft: '5px',
        paddingRight: '5px',
        minWidth: '60px',
    },
    exportName: {
        marginTop: '5px',
    },
    marginLeft: {
        marginLeft: theme.spacing(3),
    },
    ...Animations.Push(theme),
    warningBox: {
        border: `1px solid ${theme.palette.warning.main}`,
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
    },
}));

const Event = () => {
    const classes = useStyles();
    const params = useParams();
    const location = useLocation();
    const syslogRef = useRef([]);
    const elkRef = useRef([]);
    const rawRef = useRef([]);
    const zeekRef = useRef([]);
    const netflowRef = useRef([]);
    const ipfixRef = useRef([]);
    const kafkaRef = useRef([]);
    const [addExport, setAddExport] = useState(false);
    const [addExportName, setAddExportName] = useState('');
    const [original, setOriginal] = useState({});
    const [changes, setChanges] = useState(false);
    const [exports, setExports] = useState({ count: 0, name: '', type: '', data: {} });
    const [eventExportsOpen, setEventExportsOpen] = useState([]);
    const [mode, setMode] = useState('eexportd_conf');
    const [template, setTemplate] = useState('Default');
    const [newExports, setNewExports] = useState({});
    const [valid, setValid] = useState({});
    const [pushing, setPushing] = useState(false);
    const exportsPerms = Auth.permissions.get('ExportsPolicy');
    const [readonly, setReadonly] = useState(!exportsPerms.write);

    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

    useEffect(() => {
        setChanges(!_isEqual(exports, original));
    }, [exports, original]);

    const getExports = useCallback(() => {
        PolicyService.getPolicy('eexportd_conf', params.policy).then(res => {
            setEventExportsOpen(Object.keys(res.data).map(() => false));
            setValid(Object.keys(res.data).reduce((a, c) => {
                a[c] = true;
                return a;
            }, {}));
            setOriginal(_cloneDeep(res));
            setExports(_cloneDeep(res));
            setReadonly(readonly || (exportsPerms.override ? false : (!MC2CHECK && (res.source === 'mc2' || location.pathname.includes('.mc2')))));
        }, err => {
            if (typeof err === 'string') {
                toast.error(err);
            }
        });
    }, [MC2CHECK, location.pathname, params.policy, readonly]);

    useEffect(() => {
        getExports();
    }, [getExports]);

    const handleChanges = (name, changes, data) => {
        setValid({
            ...valid,
            [name]: !changes,
        });

        const e = { ...exports };
        e.data[name] = data;
        setExports(e);
    };

    const handleAddExport = () => {
        setAddExport(true);
    };

    const handleDeleteExport = (e, key) => {
        e.stopPropagation();
        const x = { ...exports };
        delete x.data[key];
        setExports(x);
    };

    const handleSave = (key, i, close) => {
        let d = null;
        const x = { ...exports };

        switch (exports.data[key].export_type) {
            case 'syslog':
                d = syslogRef.current[i].getData();
                break;
            case 'elk':
                d = elkRef.current[i].getData();
                break;
            case 'raw':
                d = rawRef.current[i].getData();
                break;
            case 'bro':
                d = zeekRef.current[i].getData();
                break;
            case 'netflow':
                d = netflowRef.current[i].getData();
                break;
            case 'ipfix':
                d = ipfixRef.current[i].getData();
                break;
            case 'kafka':
                d = kafkaRef.current[i].getData();
                break;
        }

        x.data[key] = d;
        setExports(x);

        if (close) {
            const ev = [...eventExportsOpen];
            ev[i] = false;
            setEventExportsOpen(ev);
        }

        setValid({ ...valid, [key]: true });
        toast.success('Export verification successful');
    };

    const handleTabChange = (key, t) => {
        const x = { ...exports };
        x.data[key].export_type = t;
        setExports(x);
    };

    const handleUpdateAddExportName = (e) => {
        setAddExportName(e.target.value);
    };

    const handleCreateExport = () => {
        const x = { ...exports };
        const o = { ...original };
        const exo = [...eventExportsOpen];

        if (!Object.keys(x.data).includes(addExportName)) {
            x.data[addExportName] = _cloneDeep(defaultConfigs[template]);
            o.data[addExportName] = _cloneDeep(defaultConfigs[template]);
            setNewExports({ ...newExports, [addExportName]: template });
            setExports(x);
            setOriginal(o);
            exo.push(false);
            setEventExportsOpen(exo);
            setAddExport(false);
            setAddExportName('');
            setTemplate('Default');
        } else {
            toast.error('That name is already in use, please choose another...');
        }
    };

    const handleCancelCreateExport = () => {
        setAddExport(false);
        setAddExportName('');
        setTemplate('Default');
    };

    const handleUpdateEventExportOpen = (i) => {
        const ev = [...eventExportsOpen];
        ev[i] = !ev[i];
        setEventExportsOpen(ev);
    };

    const handlePushPolicy = () => {
        setMode('push');
    };

    const handleDeployPolicy = (sensorList) => {
        setPushing(true);
        PolicyService.updatePolicy('eexportd_conf', params.policy, exports).then(res => {
            setMode('eexportd_conf');
            toast.success('Policy saved successfully');
            getExports();

            if (sensorList.length || Object.keys(sensorList).length) {
                PolicyService.assignPolicy('eexportd_conf', params.policy, sensorList).then(() => {
                    toast.success('Policy pushed to new sensor(s) successfully');
                }, err => {
                    if (typeof err === 'string') {
                        toast.error('Pushing policy to new sensor(s) failed : ' + err);
                    } else {
                        toast.error(err.message + ', ' + err.error);
                    }
                });
            }
        }, err => {
            if (err) {
                toast.error(err.message + ': ' + err.error);
            }
        }).finally(() => { setPushing(false) });
    };

    const handleFilterReference = () => {
        window.open('/support/filter_reference');
    };

    const handleRevert = () => {
        getExports();
    };

    const handleCancel = (k, i) => {
        const x = { ...exports };
        if (Object.prototype.hasOwnProperty.call(original.data, k)) {
            x.data[k] = original.data[k];
        } else {
            if (newExports[k]) {
                x.data[k] = _cloneDeep(defaultConfigs[newExports[k]]);
            } else {
                x.data[k] = _cloneDeep(defaultConfigs.Default);
            }
        }
        setExports(x);

        const ev = [...eventExportsOpen];
        ev[i] = false;
        setEventExportsOpen(ev);
        setValid({ ...valid, [k]: true });
    };

    const handleModeChange = () => {
        setMode('eexportd_conf');
    };

    const handleSetTemplate = (e) => {
        setTemplate(e.target.value);
    };

    return (
        <Grid container spacing={3} style={{ marginBottom: '32px' }}>
            <Grid container item xs={12} style={{ display: mode === 'push' ? 'none' : 'flex' }}>
                <Grid item xs={12}>
                    {!MC2CHECK && exports.source === 'mc2' && <Box mb={1} className={classes.warningBox} display="inline-block">
                        You cannot modify or push a policy assigned by an MC2.
                    </Box>}
                    {!readonly && <Button
                        className={classes.buttonRight}
                        variant="contained"
                        aria-label="revert"
                        color="secondary"
                        onClick={handleRevert}
                        disabled={!MC2CHECK && exports.source === 'mc2'}
                    >
                        Revert
                    </Button>}
                    {exportsPerms.admin && <Button
                        variant="contained"
                        aria-label="push"
                        color="primary"
                        onClick={handlePushPolicy}
                        disabled={(!MC2CHECK && exports.source === 'mc2') || !(Object.keys(valid).reduce((a, c) => {
                            if (!a) {
                                return false;
                            } else {
                                return valid[c];
                            }
                        }, true))}
                        className={clsx({
                            [classes.buttonRight]: true,
                            [classes.pushAnimation]: !(!MC2CHECK && exports.source === 'mc2') && changes && (Object.keys(valid).reduce((a, c) => {
                                if (!a) {
                                    return false;
                                } else {
                                    return valid[c];
                                }
                            }, true)),
                        })}
                    >Push</Button>}
                    {!readonly && <Button
                        className={classes.buttonRight}
                        color="primary"
                        variant="contained"
                        aria-label="export"
                        onClick={handleAddExport}
                        disabled={!MC2CHECK && exports.source === 'mc2'}
                    >
                        <AddIcon/> Export
                    </Button>}
                    <Box><IconButton
                        className={classes.buttonRight}
                        aria-label="filter reference info"
                        onClick={handleFilterReference}
                        size="large"><InfoIcon /></IconButton></Box>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Box hidden={!addExport}>
                    <Paper>
                        <Box p={3}>
                            <Box display="flex">
                                <TextField
                                    value={addExportName}
                                    onChange={handleUpdateAddExportName}
                                    aria-label="export configuration name"
                                    placeholder="Export Configuration Name"
                                    fullWidth
                                />
                                <Box ml={2} mt={-2}>
                                    <InputLabel id="export-templates">Templates</InputLabel>
                                    <Select
                                        labelId="export-templates"
                                        style={{ width: '200px' }}
                                        value={template}
                                        onChange={handleSetTemplate}
                                    >
                                        {Object.keys(defaultConfigs).map((v, i) => {
                                            return (
                                                <MenuItem key={'default-config-' + i} value={v}>{v}</MenuItem>
                                            );
                                        })}
                                    </Select>
                                </Box>
                            </Box>
                            <Box mt={3} display="flex" justifyContent="flex-end">
                                <Box ml={2}><Button variant="contained" color="secondary" aria-label="cancel" onClick={handleCancelCreateExport}>Cancel</Button></Box>
                                <Box ml={2}><Button
                                    variant="contained"
                                    color="primary"
                                    aria-label="create"
                                    onClick={handleCreateExport}
                                    disabled={!addExportName}
                                >Create</Button></Box>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </Grid>
            <Grid item xs={12} style={{ display: mode === 'push' ? '' : 'none' }}>
                {exportsPerms.admin && <Push
                    policyName={params.policy}
                    policyType="eexportd_conf"
                    onDeploy={handleDeployPolicy}
                    closePush={handleModeChange}
                    pushing={pushing}
                    permName={'ExportsPolicy'}
                />}
            </Grid>
            <Grid container item xs={12} spacing={2} style={{ display: mode === 'eexportd_conf' ? '' : 'none' }}>
                {Object.keys(exports.data).map((k, i) => {
                    if (exports.data[k]) {
                        return (
                            <Grid key={'eexportd-' + i} item xs={12}>
                                <Accordian expanded={eventExportsOpen[i]}>
                                    <AccordianSummary aria-label="export summary" onClick={() => { handleUpdateEventExportOpen(i) }}>
                                        <Grid container>
                                            <Grid item xs={12}>
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography variant="h5">
                                                        Config: <code>{k}</code> <span className={classes.marginLeft}>Export: {exports.data[k].export_type === 'bro' ? 'zeek' : exports.data[k].export_type}</span>
                                                    </Typography>
                                                    {exportsPerms.delete &&
                                                        <IconButton
                                                            className={classes.buttonRight}
                                                            color="secondary"
                                                            aria-label="delete export"
                                                            onClick={(e) => { handleDeleteExport(e, k) }}
                                                            size="large"><DeleteIcon /></IconButton>
                                                    }
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Box value={exports.data[k].export_type} hidden={exports.data[k].export_type !== 'syslog'}>
                                                    <span>Host: {exports.data[k].dest.host}</span>
                                                    <span className={classes.marginLeft}>Port: {exports.data[k].dest.port}</span>
                                                    <span className={classes.marginLeft}>Proto: {exports.data[k].dest.proto}</span>
                                                    <span className={classes.marginLeft}>Facility: {exports.data[k].syslog.facility}</span>
                                                    <span className={classes.marginLeft}>Severity: {exports.data[k].syslog.severity}</span>
                                                    <span className={classes.marginLeft}>RFC: {exports.data[k].syslog.rfc}</span>
                                                    <span className={classes.marginLeft}>Dest TZ: {exports.data[k].syslog.dst_tz}</span>
                                                </Box>
                                                <Box value={exports.data[k].export_type} hidden={exports.data[k].export_type !== 'elk'}>
                                                    <span>Dest: {exports.data[k].dest.url}</span>
                                                    <span className={classes.marginLeft}>Index:  {exports.data[k].elk.index}</span>
                                                    <span className={classes.marginLeft}>Rotate: {exports.data[k].elk.index_rotate_days}</span>
                                                    <span className={classes.marginLeft}>Dest TZ: {exports.data[k].elk.dst_tz}</span>
                                                </Box>
                                                <Box value={exports.data[k].export_type} hidden={exports.data[k].export_type !== 'kafka'}>
                                                    <span>Brokers: {(exports.data[k].kafka && exports.data[k].kafka.brokers) || ''}</span>
                                                    <span className={classes.marginLeft}>Topic:  {(exports.data[k].kafka && exports.data[k].kafka.topic) || ''}</span>
                                                </Box>
                                                <Box value={exports.data[k].export_type} hidden={exports.data[k].export_type === 'syslog' || exports.data[k].export_type === 'elk' || exports.data[k].export_type === 'kafka'}>
                                                    <span>Host: {exports.data[k].dest.host}</span>
                                                    <span className={classes.marginLeft}>Port: {exports.data[k].dest.port}</span>
                                                    <span className={classes.marginLeft}>Proto: {exports.data[k].dest.proto}</span>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <Box >
                                        </Box>
                                    </AccordianSummary>
                                    <AccordianDetails aria-label="export details">
                                        <Grid container>
                                            <Grid item xs={12}>
                                                <AppBar position="static" className={classes.tabBar}>
                                                    <Box display="flex">
                                                        <Box className={classes.appbarItem} pr={5}>Export:</Box>
                                                        <Tabs textColor='inherit' indicatorColor='secondary' value={exports.data[k].export_type} onChange={(e, t) => { handleTabChange(k, t) }}>
                                                            <Tab disabled={readonly} className={classes.tab} aria-label="syslog" value="syslog" label="Syslog" />
                                                            <Tab disabled={readonly} className={classes.tab} aria-label="elk" value="elk" label="ES REST" />
                                                            <Tab disabled={readonly} className={classes.tab} aria-label="raw" value="raw" label="Raw" />
                                                            <Tab disabled={readonly} className={classes.tab} aria-label="bro" value="bro" label="Zeek" />
                                                            <Tab disabled={readonly} className={classes.tab} aria-label="netflow" value="netflow" label="Netflow v9" />
                                                            <Tab disabled={readonly} className={classes.tab} aria-label="ipfix" value="ipfix" label="IPFIX" />
                                                            <Tab disabled={readonly} className={classes.tab} aria-label="kafka" value="kafka" label="Kafka" />
                                                        </Tabs>
                                                    </Box>
                                                </AppBar>
                                                <Box p={2} className={classes.tabBG} value={exports.data[k].export_type} index={0} hidden={exports.data[k].export_type !== 'syslog'}>
                                                    {exports.data[k].export_type === 'syslog' && <Syslog ref={ref => { syslogRef.current[i] = ref }} name={k} index={i} data={exports.data[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} policySource={exports.source} perms={exportsPerms} />}
                                                </Box>
                                                <Box p={2} className={classes.tabBG} value={exports.data[k].export_type} index={1} hidden={exports.data[k].export_type !== 'elk'}>
                                                    {exports.data[k].export_type === 'elk' && <ESRest ref={ref => { elkRef.current[i] = ref }} name={k} index={i} data={exports.data[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} policySource={exports.source} perms={exportsPerms} />}
                                                </Box>
                                                <Box p={2} className={classes.tabBG} value={exports.data[k].export_type} index={2} hidden={exports.data[k].export_type !== 'raw'}>
                                                    {exports.data[k].export_type === 'raw' && <Raw ref={ref => { rawRef.current[i] = ref }} name={k} index={i} data={exports.data[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} policySource={exports.source} perms={exportsPerms} />}
                                                </Box>
                                                <Box p={2} className={classes.tabBG} value={exports.data[k].export_type} index={3} hidden={exports.data[k].export_type !== 'bro'}>
                                                    {exports.data[k].export_type === 'bro' && <Zeek ref={ref => { zeekRef.current[i] = ref }} name={k} index={i} data={exports.data[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} policySource={exports.source} perms={exportsPerms} />}
                                                </Box>
                                                <Box p={2} className={classes.tabBG} value={exports.data[k].export_type} index={4} hidden={exports.data[k].export_type !== 'netflow'}>
                                                    {exports.data[k].export_type === 'netflow' && <Netflow ref={ref => { netflowRef.current[i] = ref }} name={k} index={i} data={exports.data[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} policySource={exports.source} perms={exportsPerms} />}
                                                </Box>
                                                <Box p={2} className={classes.tabBG} value={exports.data[k].export_type} index={5} hidden={exports.data[k].export_type !== 'ipfix'}>
                                                    {exports.data[k].export_type === 'ipfix' && <IPFix ref={ref => { ipfixRef.current[i] = ref }} name={k} index={i} data={exports.data[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} policySource={exports.source} perms={exportsPerms} />}
                                                </Box>
                                                <Box p={2} className={classes.tabBG} value={exports.data[k].export_type} index={5} hidden={exports.data[k].export_type !== 'kafka'}>
                                                    {exports.data[k].export_type === 'kafka' && <Kafka ref={ref => { kafkaRef.current[i] = ref }} name={k} index={i} data={exports.data[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} policySource={exports.source} perms={exportsPerms} />}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </AccordianDetails>
                                </Accordian>
                            </Grid>
                        );
                    }
                    return null;
                })}
            </Grid>
        </Grid>
    );
};

export default Event;
